import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

// G1 — public proof page for the AI grader. Shows the verified-first hit-rate
// (within ±1 grade), sample counts, per-band corrections, and a recent log of
// verified prediction-vs-actual pairs. The trust marketing standalone grading
// apps lead with — but ours is attached to a store.
export default function GradeAccuracy() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    fetch('/api/grading/accuracy')
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then(setData)
      .catch(() => setErr(true));
  }, []);

  return (
    <div className="page" style={{ maxWidth: 860, margin: '0 auto', padding: 24 }}>
      <SEO
        title="AI grading accuracy — verified against real PSA grades"
        description="How our free AI pre-grader stacks up against real PSA grades: verified hit-rate within one grade, sample size, and a live log of predictions vs actuals."
      />
      <p style={{ marginBottom: 4 }}><Link to="/grade">← Back to the grader</Link></p>
      <h1>How accurate is our AI grade?</h1>
      <p style={{ color: 'var(--text-light)' }}>
        We publish this honestly. Every figure below is computed from real grades
        that came back from PSA and were <strong>verified</strong> — not self-reported guesses.
      </p>

      {err && <p>Accuracy data isn’t available right now.</p>}
      {!err && !data && <p>Loading…</p>}

      {data && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, margin: '20px 0' }}>
            <Stat
              label="Within ±1 grade"
              value={data.hitRateWithin1 != null ? `${data.hitRateWithin1}%` : '—'}
              hint={data.usingVerified ? 'verified sample' : 'early sample'}
            />
            <Stat label="Verified grades" value={data.verifiedSamples ?? 0} hint="real PSA certs" />
            <Stat label="Total feedback" value={data.samples ?? 0} hint="all reports" />
          </div>

          {data.verifiedSamples < 5 && (
            <div className="page__panel" style={{ background: 'var(--color-info-bg, #eff6ff)' }}>
              We’re still gathering verified grades — accuracy figures sharpen as more
              members report their real PSA results. Got a graded card you scanned here?{' '}
              <Link to="/grade">Add the cert to help.</Link>
            </div>
          )}

          {data.bands && Object.keys(data.bands).length > 0 && (
            <section style={{ marginTop: 24 }}>
              <h2>Per-grade calibration</h2>
              <p style={{ color: 'var(--text-light)' }}>
                Where our heuristics run consistently high or low at a given grade, we
                correct for it automatically. A negative number means we nudge that band down.
              </p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {Object.entries(data.bands).sort((a, b) => Number(b[0]) - Number(a[0])).map(([band, b]) => (
                  <span key={band} style={{ padding: '6px 10px', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 13 }}>
                    Grade {band}: <strong>{b.bias > 0 ? '+' : ''}{b.bias}</strong> <small style={{ color: 'var(--text-light)' }}>({b.n})</small>
                  </span>
                ))}
              </div>
            </section>
          )}

          {data.recent?.length > 0 && (
            <section style={{ marginTop: 24 }}>
              <h2>Recent verified results</h2>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 420 }}>
                  <thead><tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                    <th align="left" style={{ padding: 8 }}>We predicted</th>
                    <th align="left" style={{ padding: 8 }}>PSA graded</th>
                    <th align="left" style={{ padding: 8 }}>Δ</th>
                    <th align="left" style={{ padding: 8 }}>Grader</th>
                  </tr></thead>
                  <tbody>
                    {data.recent.slice(0, 40).map((r, i) => {
                      const delta = Number(r.actual) - Number(r.predicted);
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid var(--border-color)' }}>
                          <td style={{ padding: 8 }}>{r.predicted}</td>
                          <td style={{ padding: 8 }}>{r.actual}</td>
                          <td style={{ padding: 8, color: Math.abs(delta) <= 1 ? 'var(--color-success-dark, #137333)' : 'var(--color-danger, #dc2626)' }}>
                            {delta > 0 ? '+' : ''}{Math.round(delta * 10) / 10}
                          </td>
                          <td style={{ padding: 8 }}>{r.grader}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value, hint }) {
  return (
    <div className="page__panel" style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 30, fontWeight: 800, color: 'var(--primary-color)' }}>{value}</div>
      <div style={{ fontWeight: 600 }}>{label}</div>
      {hint && <div style={{ fontSize: 12, color: 'var(--text-light)' }}>{hint}</div>}
    </div>
  );
}
